import Vue from 'vue'

import _ from 'lodash'
import { isFuture, endOfMonth, parse } from 'date-fns'

// import { Environment, getCurrentEnvironment } from '~utils/environment'
import cancellationUrlWithError from '~utils/psp-cancellation'

import { Redemption } from '~models/redemption'
import SecureFields from '~plugins/secure-fields.ts'

const SECURE_3D_FAILURE_CODES = ['E', 'N']

interface SecureFieldDefinition {
  focused: boolean;
  valid: boolean;
  secure: boolean;
  value?: string;
  filled?: boolean;
  datatransValidated?: boolean;
}

export default Vue.extend({
  data(): {
    formReady: boolean;
    fields: Record<string, SecureFieldDefinition>;
    secureFieldsProcessing: boolean;
    secureFields?: any;
  } {
    return {
      formReady: false,
      fields: {
        expiryDate: {
          value: '',
          focused: false,
          valid: true,
          secure: false,
        },
        name: {
          value: '',
          focused: false,
          valid: true,
          secure: false,
        },
        cvv: {
          focused: false,
          filled: false,
          valid: true,
          secure: true,
          datatransValidated: false,
        },
        cardNumber: {
          focused: false,
          filled: false,
          valid: true,
          secure: true,
          datatransValidated: false,
        },
      },
      secureFieldsProcessing: false,
      secureFields: undefined,
    }
  },

  computed: {
    datatransStyles(): Record<string, string> {
      return {}
    },
    datatransBorder(): string | undefined {
      return undefined
    },
    datatransBorderError(): string | undefined {
      return undefined
    },
    errors(): string[] {
      return this.$store.state.redemptions.errors
    },
    firstErrorKey(): string {
      return this.errors[0]
    },
    redemption(): Redemption {
      return this.$store.state.redemptions.redemption
    },
    loading(): boolean {
      return this.$store.state.redemptions.loading
    },
    paymentProcessing(): boolean {
      return this.$store.state.redemptions.paymentProcessing
    },
    redemptionUltimateFailureReason(): string {
      return this.$store.state.redemptions.ultimateFailureReason
    },
    currency(): string | undefined {
      return this.redemption.paymentSummary?.currency?.toUpperCase()
    },
    market(): string | undefined {
      return this.redemption.countryOfDeparture
    },
    remainingFlightVoucherBalance(): number {
      const remainingBalance = (this.redemption.vouchersBalance || 0) - this.redemption.amount

      return remainingBalance < 0 ? 0 : remainingBalance
    },
    cardExpirationMonth(): string {
      return _.get(this.fields, ['expiryDate', 'value'], '').split('/')[0]
    },
    cardExpirationYear(): string {
      return _.get(this.fields, ['expiryDate', 'value'], '').split('/')[1]
    },
    status3DSecure(): string {
      return this.$route.query.status_3d as string
    },
    locale(): string {
      return this.$store.getters['locale/currentLocale']
    },
    innerUltimateFailure(): boolean {
      return this.errors.includes('payment_service_too_many_reinitializations')
    },
  },
  watch: {
    cardExpirationYear(value) {
      if (/^\d\d$/i.test(value)) {
        this.secureFields.focus('cvv')
      }
    },
    locale() {
      this.secureFields.setPlaceholder('cardNumber', this.$t('components.payment.form.cardNumber.label'))
      this.secureFields.setPlaceholder('cvv', this.$t('components.payment.form.cvv.label'))
    },
    'fields.cardNumber.valid': {
      handler(value) {
        this.setSecureFieldStyle('cardNumber', value)
      },
    },
    'fields.cvv.valid': {
      handler(value) {
        this.setSecureFieldStyle('cvv', value)
      },
    },
  },
  async created() {
    if (this.status3DSecure) {
      window.history.replaceState({}, document.title, this.$route.path)

      if (SECURE_3D_FAILURE_CODES.indexOf(this.status3DSecure) !== -1) {
        await this.$store.dispatch('redemptions/setErrors', {
          errors: ['secure3d'],
        })

        this.initializeForm()
      } else {
        await this.confirmRedemption(this.redemption.datatransTransactionId as string, true)
      }
    }
  },

  mounted() {
    if (!this.status3DSecure) {
      this.initializeForm()
    }
  },

  methods: {
    setSecureFieldStyle(field: 'cvv' | 'cardNumber', valid: boolean) {
      if (!this.datatransBorder || !this.datatransBorderError) {
        return
      }

      this.secureFields.setStyle(field, `border-color: ${valid ? this.datatransBorder : this.datatransBorderError}`)
    },
    resetNormalFields() {
      _.forEach(this.fields, (field, fieldName) => {
        if (field.secure) {
          this.fields[fieldName] = {
            focused: false,
            filled: false,
            valid: true,
            secure: true,
            datatransValidated: false,
          }
        } else {
          this.fields[fieldName] = {
            value: '',
            focused: false,
            valid: true,
            secure: false,
          }
        }
      })
    },
    initializeSecureFields() {
      this.secureFieldsProcessing = false
      this.secureFields = new SecureFields()

      Vue.nextTick(() => {
        this.secureFields.init(
          this.redemption.datatransTransactionId,
          {
            cardNumber: {
              placeholderElementId: 'card-number',
              placeholder: this.$t('components.payment.form.cardNumber.label'),
              inputType: 'tel',
            },
            cvv: {
              placeholderElementId: 'cvv-number',
              placeholder: this.$t('components.payment.form.cvv.label'),
              inputType: 'tel',
            },
          },
          {
            styles: this.datatransStyles,
            // debug: getCurrentEnvironment() === Environment.Development,
            debug: false,
          },
        )
      })
    },
    subscribeSecureFieldsEvents() {
      this.secureFields.on('validate', (event: any) => {
        if (event.hasErrors) {
          _.forEach(event.fields, ({ valid }: any, fieldName) => {
            this.fields[fieldName].valid = event.fields[fieldName].valid
          })

          this.secureFieldsProcessing = false
        }
      })

      this.secureFields.on('ready', () => {
        this.formReady = true
      })

      this.secureFields.on('change', ({ event, fields }: any) => {
        if (event.type === 'focus') {
          this.resetFieldValidity(event.field)
        }

        if (event.type === 'blur') {
          this.fields[event.field].focused = false
        }

        this.fields[event.field].datatransValidated = fields[event.field].valid
        this.fields[event.field].filled = fields[event.field].length > 0
      })

      this.secureFields.on('error', () => { this.secureFieldsProcessing = false })

      this.secureFields.on('success', async (data: any) => {
        if (data.transactionId) {
          if (data.redirect) {
            await this.$store.dispatch('redemptions/secure3dRequired', {
              redemptionId: this.redemption.id,
              datatransTransactionId: data.transactionId,
            })

            // Prevent a short UI glitch
            this.$store.dispatch('redemptions/setLoadingState', { loading: true })
            this.$store.dispatch('redemptions/setPaymentProcessingState', { paymentProcessing: true })

            if (this.innerUltimateFailure) {
              this.$store.dispatch('redemptions/setLoadingState', { loading: false })
              this.$store.dispatch('redemptions/setPaymentProcessingState', { paymentProcessing: false })
              this.tooManyReinitializationRedirect()
              return
            }

            if (this.redemption.secure3dRequired) {
              window.location = data.redirect
              return
            }
          }

          this.confirmRedemption(data.transactionId, false)
        }
      })
    },
    payment() {
      if (this.validateFields()) {
        this.secureFieldsProcessing = true

        this.secureFields.submit({
          expm: this.cardExpirationMonth,
          expy: this.cardExpirationYear,
        })
      }
    },
    async initializeForm() {
      if (!this.redemption?.paymentSummary?.fromCard) {
        this.$router.replace({ name: 'redemption-summary' })
        return
      }

      await this.$store.dispatch('redemptions/initializeDatatrans', {
        redemptionId: this.redemption.id,
      })

      if (this.redemptionUltimateFailureReason) {
        return
      }

      if (this.innerUltimateFailure) {
        this.tooManyReinitializationRedirect()

        return
      }

      this.initializeSecureFields()
      this.subscribeSecureFieldsEvents()
      this.resetNormalFields()
    },
    tooManyReinitializationRedirect() {
      const redirectTimeoutInSeconds = 5

      setTimeout(() => {
        const redirectUrl = cancellationUrlWithError(
          this.redemption.cancellationUrl, 'CARD_PAYMENT',
        )
        window.location.assign(redirectUrl)
      }, redirectTimeoutInSeconds * 1000)
    },
    async confirmRedemption(datatransTransactionId: string, redirectRequired: boolean) {
      await this.$store.dispatch('redemptions/checkout', {
        redemptionId: this.redemption.id,
        appliedVouchersBalance: this.redemption.vouchersBalance,
        datatransTransactionId,
        redirectRequired,
      })

      if (_.isEmpty(this.errors)) {
        window.location.assign(this.redemption.confirmationUrl)
      } else {
        await this.initializeForm()
      }
    },
    validateFields() {
      const validators: Record<string, (value: string) => boolean> = {
        name: (value: string) => [/^[\u00C0-\u017Fa-zA-Z']+([\s-]?[\u00C0-\u017Fa-zA-Z']+\.?)*$/i, /^.{2,40}$/].every((pattern) => pattern.test(value)),
        expiryDate: (value: string) => {
          if (/^[01]\d\/\d\d$/i.test(value)) {
            if (isFuture(endOfMonth(parse(value, 'MM/yy', new Date())))) {
              return true
            }
          }
          return false
        },
      }
      _.forEach(this.fields, (field, fieldName: string) => {
        if (!field.secure) {
          const isFieldValid = validators[fieldName](this.fields[fieldName].value as string)
          this.fields[fieldName].valid = isFieldValid
        } else {
          this.fields[fieldName].valid = this.fields[fieldName].datatransValidated as boolean
        }
      })

      return _.every(this.fields, (field) => field.valid)
    },
    resetFieldValidity(fieldName: string) {
      Object.assign(this.fields[fieldName], {
        valid: true,
        focused: true,
      })
    },
  },
})
